// 📄 pages/Proyectos/components/ChecklistStepPicker.tsx
// Selector en cascada Checklist > Canal > Paso para vincular un incidente
// (fase "Proyecto" + solución "commerce") a un paso concreto del checklist
// de Commerce Automation. Extraído para reusarse tanto al crear (ver
// IncidenteFormModal.tsx, que mantiene su propia copia de esta lógica) como
// al editar un incidente (IncidenteDetailModal.tsx).
import React, { useEffect, useState } from 'react';
import { COMMERCE_CHECKLISTS, buildChecklistStepKey, parseChecklistStepKey } from '../../../data/commerceChecklistCatalog.ts';
import { IncidenteFase } from '../../../services/incidenteService.ts';

interface ChecklistStepPickerProps {
  fase: IncidenteFase;
  solucionRelacionada: string;
  checklistStepKey?: string;
  onChange: (checklistStepKey: string | undefined) => void;
}

export default function ChecklistStepPicker({ fase, solucionRelacionada, checklistStepKey, onChange }: ChecklistStepPickerProps) {
  const parsed = checklistStepKey ? parseChecklistStepKey(checklistStepKey) : null;

  const [checklistId, setChecklistId] = useState(parsed?.checklist.id || COMMERCE_CHECKLISTS[0].id);
  const [canalId, setCanalId] = useState(parsed?.canal?.id || COMMERCE_CHECKLISTS[0].canales?.[0]?.id || '');
  const [stepId, setStepId] = useState(parsed?.step.id || COMMERCE_CHECKLISTS[0].canales?.[0]?.steps?.[0]?.id || '');

  const esProyectoCommerce = fase === 'Proyecto' && solucionRelacionada === 'commerce';
  const checklistActivo = COMMERCE_CHECKLISTS.find(c => c.id === checklistId) || COMMERCE_CHECKLISTS[0];
  const pasosDisponibles = checklistActivo.canales
    ? (checklistActivo.canales.find(c => c.id === canalId)?.steps || [])
    : (checklistActivo.steps || []);

  // Única fuente de verdad hacia el padre: cualquier cambio de selección (o
  // de fase/solución) recalcula y notifica la clave, incluyendo `undefined`
  // cuando deja de aplicar — así el padre nunca se queda con un
  // checklistStepKey viejo de una fase/solución distinta.
  useEffect(() => {
    onChange(esProyectoCommerce ? buildChecklistStepKey(checklistId, stepId, checklistActivo.canales ? canalId : undefined) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esProyectoCommerce, checklistId, canalId, stepId]);

  if (fase !== 'Proyecto') return null;

  if (!esProyectoCommerce) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">Paso del checklist</label>
        <select disabled className="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-2.5 text-sm text-gray-500 cursor-not-allowed">
          <option>Checklist no implementado para esta solución</option>
        </select>
      </div>
    );
  }

  const handleChecklistChange = (nuevoChecklistId: string) => {
    const nuevoChecklist = COMMERCE_CHECKLISTS.find(c => c.id === nuevoChecklistId);
    const nuevoCanalId = nuevoChecklist?.canales?.[0]?.id || '';
    const nuevosPasos = nuevoChecklist?.canales ? nuevoChecklist.canales.find(c => c.id === nuevoCanalId)?.steps : nuevoChecklist?.steps;
    setChecklistId(nuevoChecklistId);
    setCanalId(nuevoCanalId);
    setStepId(nuevosPasos?.[0]?.id || '');
  };

  const handleCanalChange = (nuevoCanalId: string) => {
    const nuevosPasos = checklistActivo.canales?.find(c => c.id === nuevoCanalId)?.steps || [];
    setCanalId(nuevoCanalId);
    setStepId(nuevosPasos[0]?.id || '');
  };

  return (
    <div className={`grid grid-cols-1 ${checklistActivo.canales ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">Checklist</label>
        <select
          value={checklistId}
          onChange={(e) => handleChecklistChange(e.target.value)}
          className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
        >
          {COMMERCE_CHECKLISTS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      {checklistActivo.canales && (
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Canal</label>
          <select
            value={canalId}
            onChange={(e) => handleCanalChange(e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
          >
            {checklistActivo.canales.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
      )}
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">
          Paso <span className="text-red-400">*</span>
        </label>
        <select
          value={stepId}
          onChange={(e) => setStepId(e.target.value)}
          className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
        >
          {pasosDisponibles.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
      </div>
    </div>
  );
}
